import { usePictureInPictureModeContext } from '@/context/InvoiceReports';
import { useBulkDeleteInvoiceReportService } from '@/context/services/invoice_reports/InvoiceReportBulkDestroy.service';
import { useTriggerFindTargetReport } from '@/features/invoice_reports/components/List/context/TriggerFindTargetReportContext';
import { useCheckInvoiceReportsProtectedResources } from '@/features/invoice_reports/components/hooks/useCheckInvoiceReportsProtectedResources';
import { extractInvoiceReportErrorMessage } from '@/features/invoice_reports/utils/extractErrorMessage';
import { useTranslation } from '@/i18n';
import { toHtmlNode } from '@/utils/toHtmlNode';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import { useCallback } from 'react';

type UseBulkDeleteInvoiceReportProps = {
  refetch: () => void | Promise<unknown>;
  onCloseBulkDeleteModal: () => void;
  selectedId?: string | null;
  onDrawerClose?: () => void;
};

const TOAST_DURATION = 5;

export const useBulkDeleteInvoiceReport = ({
  refetch,
  onCloseBulkDeleteModal,
  selectedId,
  onDrawerClose,
}: UseBulkDeleteInvoiceReportProps) => {
  const notify = useToastAPI();
  const { t } = useTranslation();
  const { onTriggerBackToPageOneIfNoData } = useTriggerFindTargetReport();
  const { mutateAsync: bulkDeleteInvoiceReport } =
    useBulkDeleteInvoiceReportService();
  const { handleCheckPermission } = useCheckInvoiceReportsProtectedResources();
  const { isPIPMode, openPictureInPicture } = usePictureInPictureModeContext();

  const bulkDeleteReports = useCallback(
    (reportIds: string[]) => async () => {
      try {
        const success = await bulkDeleteInvoiceReport(reportIds);
        if (!success) return;
        onCloseBulkDeleteModal();
        notify.success({
          title: t('invoice_reports.bulk_delete_success', {
            count: reportIds.length,
          }),
          duration: TOAST_DURATION,
        });
        if (selectedId && reportIds.includes(selectedId)) {
          if (isPIPMode) {
            await openPictureInPicture(false);
          }
          onDrawerClose?.();
        }
        await refetch?.();
        onTriggerBackToPageOneIfNoData();
      } catch (error) {
        const errorMessage = extractInvoiceReportErrorMessage(error, t);
        await handleCheckPermission({
          fnCheckPermission: refetch,
          onCheckSuccess: () => {
            onTriggerBackToPageOneIfNoData();
            notify.error({
              title: toHtmlNode(
                errorMessage ?? t('invoice_reports.bulk_delete_error')
              ),
              duration: TOAST_DURATION,
            });
          },
          onCheckFailure: () => {
            location.reload();
          },
        });
      }
    },
    [
      bulkDeleteInvoiceReport,
      handleCheckPermission,
      isPIPMode,
      notify,
      onCloseBulkDeleteModal,
      onDrawerClose,
      openPictureInPicture,
      refetch,
      selectedId,
      onTriggerBackToPageOneIfNoData,
      t,
    ]
  );

  return {
    bulkDeleteReports,
  };
};
